import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store from '../../store/store';
import {
    useAddAvailableAppointmentDayMutation,
    useUpdateAvailableAppointmentDayMutation,
    useLazyGetAvailableAppointmentDayByDoctorIdQuery,
    useRemoveAppointmentDayMutation
} from "../../store/api/availableAppointmentDayApiSlice";
import {
    addAvailableAppointmentDayToList,
    setGeneratedAvailableAppointmentDay,
    clearGeneratedAvailableApoointmentDay,
    addAvailableAppointmentDayByDoctorId,
    updatedAvailableAppointmentDayByDoctorId,
    removeTimeFromGeneratedAvailableAppointmentDay,
    removeAvailableAppointmentDayFromList,
    setIsLoaded
} from "../../store/slices/availableAppointmentDaySlices";
import { useGenerateMultipleAvailableTimesMutation } from "../../store/api/availableAppointmentTimesApiSlice";
import { useCreateAppointmentsForDoctorMutation } from "../../store/api/bookedAppointmentApiSlice";
import { Rendered, Days, AvailableTimes, HourRanges, HourRange } from "../../types/AppointmentDayTypes";
import DAYS, { MONDAY } from '../../constants/days';
import useDateFunc from '../useDateFunc';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import {
    areHourListsEqual,
    dayKeyToEnum,
    getApiErrorCode,
    getFirstApiErrorMessage,
    getHourList,
    normalizeAvailableTimes,
    normalizeDayKey,
    normalizeDoctorAvailableDaysDTO,
    normalizeTimeRanges,
} from "../../utils/appointmentDayUtils";

const useAppointmentDays = () => {
    //--------- HOOKS -----------------
    const dispatch = useDispatch();
    const { getLocaleTime, sortTimes } = useDateFunc();
    const { t } = useTranslation();
    //---------- states
    const [availableAppointmentDays, setAvailableAppointmentDays] = useState({});
    const [doctorId, setDoctorId] = useState("");
    //----- selectors
    const availableAppointmentDaysOnState = useSelector(state => state.availableAppointmentDay.list);
    const generated = useSelector(state => state.availableAppointmentDay.generated);
    //------ mutations
    const [getAvailableAppointmentDayByDoctorId] = useLazyGetAvailableAppointmentDayByDoctorIdQuery();
    const [addAvailableAppointmentDay] = useAddAvailableAppointmentDayMutation();
    const [updateAvailableAppointmentDay] = useUpdateAvailableAppointmentDayMutation();
    const [removeAppointmentDay] = useRemoveAppointmentDayMutation();
    const [generateMultipleAvailableTimes] = useGenerateMultipleAvailableTimesMutation();
    const [createAppointmentsForDoctor] = useCreateAppointmentsForDoctorMutation();
    const persistedHoursRef = useRef({});
    const rebuildLockRef = useRef(false);

    const rememberPersistedDays = (days) => {
        const next = {};
        (days || []).forEach((day) => {
            const key = normalizeDayKey(day?.day);
            if (key) next[key] = getHourList(day.availableTimes);
        });
        persistedHoursRef.current = next;
    };

    const syncBucketToList = (bucket) => {
        if (!doctorId || !bucket?.day) return;
        const dayKey = normalizeDayKey(bucket.day);
        const availableTimes = normalizeAvailableTimes(bucket.availableTimes).map(({ hour }) => ({ hour }));
        const existing = getSavedDayFromStore(dayKey);
        const id = bucket.id || existing?.id || null;
        if (!id && availableTimes.length === 0) return;
        const data = { id, day: dayKey, availableTimes };
        if (id) {
            dispatch(updatedAvailableAppointmentDayByDoctorId({ doctorId, data }));
            return;
        }
        dispatch(addAvailableAppointmentDayByDoctorId({ doctorId, data }));
    };

    const showApiError = useCallback((err, fallbackKey = "error_occured") => {
        const code = getApiErrorCode(err);
        if (code === "SFE-00001") {
            toast.error(t("service_fee_required_for_availability"));
            return { code, message: t("service_fee_required_for_availability") };
        }
        if (code === "GNR-00004") {
            toast.error(t("check_time_ranges"));
            return { code, message: t("check_time_ranges") };
        }
        const message = getFirstApiErrorMessage(err) || t(fallbackKey);
        toast.error(message);
        return { code, message };
    }, [t]);

    //------ FUNCTIONS -----------------
    const removeDuplicates = (arr) => {
        var seen = {};
        var out = [];
        var len = arr.length;
        var j = 0;
        for (var i = 0; i < len; i++) {
            var item = arr[i];
            if (seen[item.hour] !== 1) {
                seen[item.hour] = 1;
                out[j++] = item;
            }
        }
        return out;
    }
    /**
     *
     * @param {Days} data
     * @returns
     */
    const getSavedDayFromStore = (dayKey) => {
        const list = store.getState().availableAppointmentDay.list || [];
        const entry = list.find((item) => String(item.doctorId) === String(doctorId));
        return (entry?.days || []).find((d) => normalizeDayKey(d.day) === dayKey) || null;
    }
    const isAppointmentDayAlreadyExists = (err) => {
        if (getApiErrorCode(err) === "ADY-00002") return true;
        const message = getFirstApiErrorMessage(err);
        return typeof message === "string"
            && message.toLowerCase().includes("appointment day already exist");
    }
    const stampGeneratedDayId = (dayKey, id) => {
        if (!dayKey || !id) return;
        const currentGenerated = store.getState().availableAppointmentDay.generated;
        const nextGenerated = { ...currentGenerated };
        let changed = false;
        for (const key of ["prev", "curr", "next"]) {
            const bucket = currentGenerated?.[key];
            if (normalizeDayKey(bucket?.day) === dayKey && bucket?.id !== id) {
                nextGenerated[key] = { ...bucket, id };
                changed = true;
            }
        }
        if (changed) dispatch(setGeneratedAvailableAppointmentDay(nextGenerated));
    }
    const bucketWithSavedId = (bucket, availableTimes) => {
        const dayKey = normalizeDayKey(bucket?.day);
        const latest = store.getState().availableAppointmentDay.generated;
        const latestBucket = ["prev", "curr", "next"]
            .map((key) => latest?.[key])
            .find((item) => normalizeDayKey(item?.day) === dayKey);
        const saved = getSavedDayFromStore(dayKey);
        return {
            ...bucket,
            id: bucket?.id || latestBucket?.id || saved?.id || null,
            availableTimes,
        };
    }
    /** Gün sunucuda varken liste kimliği kaybolmuşsa kaydı bulur. Boş liste mevcut saatleri silmez. */
    const adoptExistingAppointmentDayAsync = async (dayKey, availableTimes) => {
        if (!doctorId) return false;
        try {
            const res = await getAvailableAppointmentDayByDoctorId(doctorId).unwrap();
            if (!res?.isSuccess) return false;
            const normalized = normalizeDoctorAvailableDaysDTO(res.data);
            dispatch(addAvailableAppointmentDayToList(normalized));
            const saved = (normalized.days || []).find((day) => normalizeDayKey(day.day) === dayKey);
            if (!saved?.id) return false;
            stampGeneratedDayId(dayKey, saved.id);
            const times = normalizeAvailableTimes(availableTimes);
            if (times.length === 0) return true;
            return updateAvailableAppointmentDayAsync({
                id: saved.id,
                day: dayKey,
                availableTimes: times,
            });
        } catch (err) {
            console.error("[useAppointmentDays] adopt existing day error:", err);
            return false;
        }
    }
    /**
     * Save generated.curr. Reads the latest store state so it is safe to call from timers.
     * @param {{ refetch?: boolean }} options
     */
    const saveChangesAsync = async ({ refetch = true } = {}) => {
        const currentGenerated = store.getState().availableAppointmentDay.generated;
        const currDay = currentGenerated?.curr;
        if (!currDay?.day) {
            return { success: false, isUpdate: false };
        }

        if (rebuildLockRef.current) {
            return { success: true, isUpdate: false, skipped: true };
        }

        const dayKey = normalizeDayKey(currDay.day);
        const savedDay = getSavedDayFromStore(dayKey);
        const persisted = persistedHoursRef.current[dayKey] || [];
        if (areHourListsEqual(persisted, currDay.availableTimes)) {
            return { success: true, isUpdate: false, skipped: true };
        }

        let currentId = currDay.id || savedDay?.id || availableAppointmentDays[dayKey]?.id || null;
        const availableTimes = normalizeAvailableTimes(currDay.availableTimes);

        if (currentId && availableTimes.length === 0) {
            try {
                const res = await removeAppointmentDay(currentId).unwrap();
                if (!res.isSuccess) {
                    return { success: false, isUpdate: false };
                }
                dispatch(removeAvailableAppointmentDayFromList({ id: currentId }));
                setAvailableAppointmentDays((prev) => {
                    const updated = { ...prev };
                    delete updated[dayKey];
                    return updated;
                });
                const latestGenerated = store.getState().availableAppointmentDay.generated;
                if (normalizeDayKey(latestGenerated?.curr?.day) === dayKey) {
                    dispatch(setGeneratedAvailableAppointmentDay({
                        ...latestGenerated,
                        curr: { ...latestGenerated.curr, id: null },
                    }));
                }
                persistedHoursRef.current = {
                    ...persistedHoursRef.current,
                    [dayKey]: [],
                };
                if (refetch) await refetchAppointmentDaysAsync(dayKey);
                return { success: true, isUpdate: false, deleted: true };
            } catch (err) {
                const error = showApiError(err);
                return { success: false, isUpdate: false, error };
            }
        }

        const payload = {
            ...currDay,
            id: currentId,
            day: dayKey,
            availableTimes,
        };

        const isUpdate = Boolean(currentId);
        const isSuccess = isUpdate
            ? await updateAvailableAppointmentDayAsync(payload)
            : await addAvailableAppointmentDayAsync({
                doctorId,
                day: dayKey,
                availableTimes,
            });

        if (!isSuccess) {
            return { success: false, isUpdate };
        }

        persistedHoursRef.current = {
            ...persistedHoursRef.current,
            [dayKey]: getHourList(availableTimes),
        };
        if (refetch) await refetchAppointmentDaysAsync(dayKey);
        return { success: true, isUpdate };
    }
    /**
     * Update data
     * @param {Days} data
     */
    const updateAvailableAppointmentDayAsync = async (data) => {
        try {
            const dayEnum = dayKeyToEnum(data.day);
            if (!dayEnum) return false;

            const sentTimes = normalizeAvailableTimes(data.availableTimes).map(({ hour }) => ({ hour }));
            const backendData = {
                id: data.id,
                day: dayEnum,
                availableTimes: sentTimes,
            };

            const res = await updateAvailableAppointmentDay(backendData).unwrap();
            if (res.isSuccess) {
                const responseData = {
                    ...res.data,
                    id: res.data?.id || data.id,
                    day: normalizeDayKey(res.data?.day ?? data.day),
                    availableTimes: sentTimes,
                };
                dispatch(updatedAvailableAppointmentDayByDoctorId({ data: responseData, doctorId }));
                return true;
            }
            showApiError(res);
            return false;
        } catch (err) {
            showApiError(err);
            return false;
        }
    }
    /**
     * Add new Day
     * @param {Days} data
     */
    const addAvailableAppointmentDayAsync = async (data) => {
        const dayKey = normalizeDayKey(data.day);
        try {
            const dayEnum = dayKeyToEnum(dayKey);
            if (!dayEnum) return false;

            const backendData = {
                doctorId: data.doctorId,
                day: dayEnum,
                availableTimes: normalizeAvailableTimes(data.availableTimes).map(({ hour }) => ({ hour })),
            };

            const res = await addAvailableAppointmentDay(backendData).unwrap();
            if (res.isSuccess) {
                const dayData = {
                    id: res.data.id || res.data.Id,
                    day: normalizeDayKey(res.data.day ?? res.data.Day ?? dayKey),
                    availableTimes: normalizeAvailableTimes(
                        res.data.availableTimes || res.data.AvailableTimes || data.availableTimes
                    ),
                };
                dispatch(addAvailableAppointmentDayByDoctorId({ data: dayData, doctorId }));
                stampGeneratedDayId(dayData.day, dayData.id);
                return true;
            }
            if (isAppointmentDayAlreadyExists(res)) {
                const adopted = await adoptExistingAppointmentDayAsync(dayKey, data.availableTimes);
                if (!adopted) showApiError(res);
                return adopted;
            }
            showApiError(res);
            return false;
        } catch (err) {
            if (isAppointmentDayAlreadyExists(err)) {
                const adopted = await adoptExistingAppointmentDayAsync(dayKey, data.availableTimes);
                if (!adopted) showApiError(err);
                return adopted;
            }
            showApiError(err);
            return false;
        }
    }
    /**
     * Remove item from rendered
     * @param {string} hour  time format => "hh:mm"
     */
    const removeAvailableTime = (item) => {
        const [hour, index] = getLocaleTime(item, 1);
        dispatch(removeTimeFromGeneratedAvailableAppointmentDay({ hour, index }));
        const latest = store.getState().availableAppointmentDay.generated;
        const bucket = index === 1 ? latest.next : index === -1 ? latest.prev : latest.curr;
        syncBucketToList(bucket);
    }
    /**
     * Add generated data to generated.
     * One CalculateMultipleAvailableTimes request per day (prev / curr / next).
     * @param {{ prevRanges?: HourRange[], currRanges?: HourRange[], nextRanges?: HourRange[], prevRange?: HourRange, currRange?: HourRange, nextRange?: HourRange }} data
     */
    const addGeneratedTimes = async (data) => {
        const currentState = store.getState();
        const currentGenerated = currentState.availableAppointmentDay.generated;

        const holder = { ...currentGenerated };
        const prevRanges = data.prevRanges?.length
            ? data.prevRanges
            : (data.prevRange ? [data.prevRange] : []);
        const currRanges = data.currRanges?.length
            ? data.currRanges
            : (data.currRange ? [data.currRange] : []);
        const nextRanges = data.nextRanges?.length
            ? data.nextRanges
            : (data.nextRange ? [data.nextRange] : []);

        if (prevRanges.length) {
            const prev = await generateAvailableTimesAsync(prevRanges, currentGenerated.prev.day);
            const safePrev = Array.isArray(prev) ? prev : [];
            const arr = [...(currentGenerated.prev.availableTimes || []), ...safePrev];
            holder.prev = bucketWithSavedId(currentGenerated.prev, removeDuplicates(arr));
        }
        if (currRanges.length) {
            const curr = await generateAvailableTimesAsync(currRanges, currentGenerated.curr.day);
            const safeCurr = Array.isArray(curr) ? curr : [];
            const arr = [...(currentGenerated.curr.availableTimes || []), ...safeCurr];
            holder.curr = bucketWithSavedId(currentGenerated.curr, removeDuplicates(arr));
        }
        if (nextRanges.length) {
            const next = await generateAvailableTimesAsync(nextRanges, currentGenerated.next.day);
            const safeNext = Array.isArray(next) ? next : [];
            holder.next = bucketWithSavedId(
                currentGenerated.next,
                removeDuplicates([...(currentGenerated.next.availableTimes || []), ...safeNext])
            );
        }
        dispatch(setGeneratedAvailableAppointmentDay(holder));
        syncBucketToList(holder.prev);
        syncBucketToList(holder.curr);
        syncBucketToList(holder.next);
    }
    /**
     * Get generated times from backend (all ranges for one day in a single request).
     * @param {HourRange|HourRange[]} data
     * @param {string} day - Day string (MONDAY, TUESDAY, etc.)
     * @returns {AvailableTimes[]}
     */
    const generateAvailableTimesAsync = async (data, day) => {
        var result = null;
        try {
            const ranges = normalizeTimeRanges(data);
            if (ranges.length === 0) return [];

            const dayKey = normalizeDayKey(day);
            const dayEnum = dayKeyToEnum(dayKey);
            if (!dayEnum) return [];

            const existingDay = getSavedDayFromStore(dayKey);

            if (!existingDay?.id && dayKey && doctorId) {
                const addResult = await addAvailableAppointmentDayAsync({
                    doctorId,
                    day: dayKey,
                    availableTimes: [],
                });
                if (!addResult) return [];
            }

            const payload = { doctorId, day: dayEnum, ranges };
            const res = await generateMultipleAvailableTimes(payload).unwrap();
            if (res.isSuccess) {
                result = res.data;
            }
        } catch (err) {
            const code = getApiErrorCode(err);
            if (code === "ADY-00001" && day && doctorId) {
                const dayKey = normalizeDayKey(day);
                const addResult = await addAvailableAppointmentDayAsync({
                    doctorId,
                    day: dayKey,
                    availableTimes: [],
                });
                if (addResult) {
                    try {
                        const retryRes = await generateMultipleAvailableTimes({
                            doctorId,
                            day: dayKeyToEnum(dayKey),
                            ranges: normalizeTimeRanges(data),
                        }).unwrap();
                        if (retryRes.isSuccess) {
                            result = retryRes.data;
                        }
                    } catch (retryErr) {
                        showApiError(retryErr);
                    }
                }
            } else {
                showApiError(err);
            }
        }
        finally {
            if (!Array.isArray(result)) return [];
            return normalizeAvailableTimes(result);
        }
    }
    /**
     * Clear generated data
     */
    const clearSelectedDayData = () => {
        dispatch(clearGeneratedAvailableApoointmentDay(generated));
    }
    /**
     * This function will set generated state.
     * @param {string} currDayString
     * @param {Days} data
     */
    const getSelectedDayData = (currDayString, data) => {
        const len = DAYS.length;
        const dayIndex = DAYS.findIndex(x => x === currDayString);
        const prevDayString = DAYS[(dayIndex + len - 1) % len];
        const nextDayString = DAYS[(dayIndex + 1) % len];
        const daysList = Array.isArray(data?.days) ? data.days : [];
        const prevDayData = getDaysData(daysList, prevDayString)
        const currDayData = getDaysData(daysList, currDayString);
        const nextDayData = getDaysData(daysList, nextDayString);
        const result = {
            prev: prevDayData,
            curr: currDayData,
            next: nextDayData
        };
        dispatch(setGeneratedAvailableAppointmentDay(result));
    }
    /**
     *
     * @param {Days} days
     * @param {string} dayString
     * @param {int} index
     * @returns {Days} it will return selected days data
     */
    const getDaysData = (days, dayString) => {
        const daysList = Array.isArray(days) ? days : [];

        const result = daysList.reduce((accumulator, currentValue) => {
            const backendDayString = normalizeDayKey(currentValue.day);
            if (!backendDayString) return accumulator;

            if (backendDayString === dayString) {
                const availableTimes = normalizeAvailableTimes(currentValue.availableTimes);
                accumulator = {
                    ...currentValue,
                    availableTimes,
                    day: dayString,
                };
            }
            return accumulator
        }, { day: dayString, availableTimes: [], id: null });
        return result;
    }

    const refetchAppointmentDaysAsync = useCallback(async (selectedDayKey) => {
        if (!doctorId) return null;
        try {
            const res = await getAvailableAppointmentDayByDoctorId(doctorId).unwrap();
            if (!res.isSuccess) return null;
            const normalized = normalizeDoctorAvailableDaysDTO(res.data);
            rememberPersistedDays(normalized.days);
            dispatch(addAvailableAppointmentDayToList(normalized));
            const dayToSelect =
                selectedDayKey ||
                store.getState().availableAppointmentDay.generated?.curr?.day ||
                MONDAY;
            getSelectedDayData(dayToSelect, normalized);
            return normalized;
        } catch (err) {
            console.error("[useAppointmentDays] refetch error:", err);
            return null;
        }
    }, [doctorId, dispatch, getAvailableAppointmentDayByDoctorId]);
    /**
     * Removes every working day, then asks the backend to drop idle slots that no longer
     * match the empty template. Booked appointments are left in place.
     */
    const clearCalendarForSessionDurationAsync = async () => {
        rebuildLockRef.current = true;
        let failed = 0;
        try {
            const record = (store.getState().availableAppointmentDay.list || [])
                .find((item) => String(item.doctorId) === String(doctorId));
            const savedDays = (record?.days || []).filter((day) => day?.id);

            const currentGenerated = store.getState().availableAppointmentDay.generated;
            dispatch(setGeneratedAvailableAppointmentDay({
                prev: { ...currentGenerated.prev, availableTimes: [] },
                curr: { ...currentGenerated.curr, availableTimes: [] },
                next: { ...currentGenerated.next, availableTimes: [] },
            }));

            for (const dayRecord of savedDays) {
                try {
                    const removed = await removeAppointmentDay(dayRecord.id).unwrap();
                    if (!removed?.isSuccess) {
                        failed++;
                        continue;
                    }
                    dispatch(removeAvailableAppointmentDayFromList({ id: dayRecord.id }));
                } catch {
                    failed++;
                }
            }

            if (doctorId) {
                try {
                    const regenerated = await createAppointmentsForDoctor(doctorId).unwrap();
                    if (!regenerated?.isSuccess) failed++;
                } catch {
                    failed++;
                }
            }

            await refetchAppointmentDaysAsync();
            return { failed };
        } finally {
            rebuildLockRef.current = false;
        }
    }
    /**
     *
     * @param {AvailableTimes[]} availableTimes
     * @param {int} index
     * @returns {AvailableTimes[]} it will return locale available times
     */
    const getLocaleAvailableTimes = (availableTimes, index) => {
        const result = availableTimes.reduce((accumulator, currentValue) => {
            const [hour, hourIndex] = getLocaleTime(currentValue.hour);
            if (hourIndex === index) accumulator.push({ hour, isActive: currentValue?.isActive ?? true })
            return accumulator
        }, [])
        return result;
    }

    /**
     * Kaydedilmemiş değişiklikleri ilgili "gün bucket" (prev/curr/next) bazında backend'e gönderir.
     * Toggle gibi aksiyonlarda tüm sayfa "Kaydet" beklemeden anlık güncelleme için kullanılır.
     */
    const saveGeneratedBucketsAsync = useCallback(async (bucketKeys = []) => {
        if (!Array.isArray(bucketKeys) || bucketKeys.length === 0) return { success: false, isUpdate: false };

        const state = store.getState();
        const currentGenerated = state.availableAppointmentDay.generated;

        let isSuccess = true;
        for (const key of bucketKeys) {
            const bucket = currentGenerated?.[key];
            if (!bucket?.day) continue;

            const dayKey = String(bucket.day || "").toUpperCase();
            const resolvedId = bucket?.id ?? availableAppointmentDays?.[dayKey]?.id ?? null;
            const availableTimes = Array.isArray(bucket.availableTimes) ? bucket.availableTimes : [];
            const activeAvailableTimes = availableTimes.filter((x) => x?.isActive ?? true);
            console.log("[AppointmentSettings] toggle save bucket", {
                key,
                dayKey,
                resolvedId,
                activeCount: activeAvailableTimes.length,
                activeHours: activeAvailableTimes.map((x) => (typeof x === "string" ? x : x?.hour)).filter(Boolean),
            });

            try {
                // Boşsa ve kayıt varsa -> sil
                if (resolvedId && activeAvailableTimes.length === 0) {
                    const res = await removeAppointmentDay(resolvedId).unwrap();
                    if (!res?.isSuccess) {
                        isSuccess = false;
                        continue;
                    }
                    dispatch(removeAvailableAppointmentDayFromList({ id: resolvedId }));
                    dispatch(setGeneratedAvailableAppointmentDay({
                        ...currentGenerated,
                        [key]: { ...bucket, id: null, availableTimes: [] },
                    }));
                    continue;
                }

                if (resolvedId) {
                    const dataToUpdate = {
                        ...bucket,
                        id: resolvedId,
                        availableTimes: activeAvailableTimes,
                    };
                    const ok = await updateAvailableAppointmentDayAsync(dataToUpdate);
                    if (!ok) {
                        isSuccess = false;
                    }
                } else {
                    const ok = await addAvailableAppointmentDayAsync({
                        doctorId,
                        day: bucket.day,
                        availableTimes: activeAvailableTimes,
                    });
                    if (!ok) {
                        isSuccess = false;
                    }
                }
            } catch (e) {
                console.error("[AppointmentSettings] saveGeneratedBucketsAsync error:", e);
                isSuccess = false;
            }
        }

        return { success: isSuccess, isUpdate: false };
    }, [availableAppointmentDays, doctorId, dispatch]);

    const toggleAvailableTimeActive = useCallback((hourLabel) => {
        // hourLabel: "HH:mm" shown in UI (localized)
        const currentState = store.getState();
        const currentGenerated = currentState.availableAppointmentDay.generated;

        const toggleInList = (list = [], indexTag) => {
            let changed = false;
            const next = list.map((x) => {
                const [h, idx] = getLocaleTime(x?.hour);
                if (h === hourLabel && idx === indexTag) {
                    changed = true;
                    return { ...x, isActive: !(x?.isActive ?? true) };
                }
                return x;
            });
            return { changed, next };
        };

        // Try toggle in prev/curr/next based on locale index like getTotalAppointmentTimes does.
        const prevRes = toggleInList(currentGenerated?.prev?.availableTimes || [], 1);
        const currRes = toggleInList(currentGenerated?.curr?.availableTimes || [], 0);
        const nextRes = toggleInList(currentGenerated?.next?.availableTimes || [], -1);

        if (!prevRes.changed && !currRes.changed && !nextRes.changed) return;

        dispatch(setGeneratedAvailableAppointmentDay({
            ...currentGenerated,
            prev: { ...currentGenerated.prev, availableTimes: prevRes.next },
            curr: { ...currentGenerated.curr, availableTimes: currRes.next },
            next: { ...currentGenerated.next, availableTimes: nextRes.next },
        }));

        const changedBuckets = [];
        if (prevRes.changed) changedBuckets.push("prev");
        if (currRes.changed) changedBuckets.push("curr");
        if (nextRes.changed) changedBuckets.push("next");

        return { changed: true, changedBuckets };
    }, [dispatch, getLocaleTime]);
    const getTotalAppointmentTimes = useCallback((data) => {
        return sortTimes(
            [
                ...getLocaleAvailableTimes(data.prev.availableTimes, 1),
                ...getLocaleAvailableTimes(data.curr.availableTimes, 0),
                ...getLocaleAvailableTimes(data.next.availableTimes, -1)
            ]
        )
    }, [generated]);
    const rendered = useMemo(
        () => getTotalAppointmentTimes(generated),
        [generated, getTotalAppointmentTimes]
    );
    //------ USE EFFECT ----------------
    useEffect(() => {
        const result = availableAppointmentDaysOnState.reduce((accumulator, currentValue) => {
            if (currentValue.doctorId === doctorId) {
                accumulator = (currentValue.days || []).reduce((acc, currVal) => {
                    const dayKey = normalizeDayKey(currVal.day);
                    if (!dayKey) return acc;

                    acc = {
                        ...acc,
                        [dayKey]: {
                            id: currVal.id,
                            availableTimes: normalizeAvailableTimes(currVal.availableTimes),
                        }
                    }
                    return acc
                }, {})
            }
            return accumulator;
        }, {});
        setAvailableAppointmentDays(result);
    }, [availableAppointmentDaysOnState, doctorId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(setIsLoaded(false))
                const res = await getAvailableAppointmentDayByDoctorId(doctorId).unwrap();
                if (res.isSuccess) {
                    const normalized = normalizeDoctorAvailableDaysDTO(res.data);
                    rememberPersistedDays(normalized.days);
                    dispatch(addAvailableAppointmentDayToList(normalized));
                    getSelectedDayData(MONDAY, normalized);
                }
                dispatch(setIsLoaded(true))
            } catch (err) {
                console.error("[useAppointmentDays] fetch error:", err);
                dispatch(setIsLoaded(true))
            }
        }
        if (doctorId)
            fetchData();
    }, [doctorId, dispatch, getAvailableAppointmentDayByDoctorId]);

    return {
        rendered,
        setDoctorId,
        getSelectedDayData,
        getLocaleAvailableTimes,
        clearSelectedDayData,
        addGeneratedTimes,
        removeAvailableTime,
        toggleAvailableTimeActive,
        saveGeneratedBucketsAsync,
        saveChangesAsync,
        refetchAppointmentDaysAsync,
        clearCalendarForSessionDurationAsync,
    }
}

export default useAppointmentDays;